import { Screen } from "@/components/ui/screen"
import { useQuery } from "@tanstack/react-query"
import { Link, Outlet } from "@tanstack/react-router"
import { MarkerType, type Edge } from "@xyflow/react"
import { ArrowUpRight, Network, X } from "lucide-react"
import { useMemo, useState } from "react"
import { GraphCanvas, TruthBadge, declaredEdge } from "@/components/graph/canvas"
import { DatasetNodeView, type DatasetFlowNode, HEADER_HEIGHT, MapNodeView, type MapFlowNode, NODE_WIDTH, ROW_HEIGHT, TargetNodeView, type TargetFlowNode, TypeNodeView, type TypeFlowNode, typeNodeHeight } from "@/components/graph/nodes"
import { PageHeader } from "@/components/shell/page-header"
import { EmptyState, ErrorState } from "@/components/states"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { type DeclaredLineage, type TypeGraph, buildDeclaredLineage, buildTypeGraph } from "@/lib/explorer"
import { useT } from "@/lib/i18n"
import { layeredLayout } from "@/lib/layout"
import { useDefinitions } from "@/lib/queries"
import { useScope } from "@/lib/scope"
import { cn } from "@/lib/utils"

const tab = "relative -mb-px inline-flex h-11 items-center border-b-2 border-transparent text-sm text-muted-foreground transition-colors hover:text-foreground data-[status=active]:border-primary data-[status=active]:text-foreground"

export function ExplorerLayout() {
  const t = useT()
  const scope = useScope()
  return (
    <>
      <PageHeader
        crumbs={[
          <span key="e" className="flex items-center gap-4">
            <span className="font-medium text-foreground">{t("nav.explorer")}</span>
            <span className="flex gap-4">
              <Link to="/$orgId/$environmentId/explorer/types" params={scope} className={tab}>
                {t("explorer.types")}
              </Link>
              <Link to="/$orgId/$environmentId/explorer/lineage" params={scope} className={tab}>
                {t("explorer.lineage")}
              </Link>
            </span>
          </span>,
        ]}
        actions={<TruthBadge kind="declared" />}
      />
      <Screen width="full" className="relative min-h-0 flex-1 space-y-0 lg:px-0">
        <Outlet />
      </Screen>
    </>
  )
}

function GraphSkeleton() {
  const t = useT()
  return (
    <div className="flex h-full items-center justify-center gap-10 p-10" aria-busy="true" aria-label={t("explorer.layingOutGraph")}>
      {[0, 1, 2].map((i) => (
        <div key={i} className="space-y-6">
          <Skeleton className="h-28 w-56" />
          <Skeleton className="h-20 w-56" />
        </div>
      ))}
    </div>
  )
}

function typeFlow(graph: TypeGraph, positions: Map<string, { x: number; y: number }>): { nodes: TypeFlowNode[]; edges: Edge[] } {
  return {
    nodes: graph.nodes.map((n) => ({ id: n.id, type: "type", position: positions.get(n.id) ?? { x: 0, y: 0 }, data: { node: n } })),
    edges: graph.edges.map((e) => ({
      id: e.id,
      source: e.source,
      target: e.target,
      type: "smoothstep",
      label: e.kind === "link" ? e.label : e.kind,
      style: e.kind === "link" ? declaredEdge : { ...declaredEdge, strokeDasharray: "2 4", opacity: 0.7 },
      markerEnd: { type: MarkerType.ArrowClosed, width: 14, height: 14, color: "var(--subtle-foreground)" },
    })),
  }
}

const typeNodeTypes = { type: TypeNodeView }

export function TypeGraphPage() {
  const t = useT()
  const scope = useScope()
  const defs = useDefinitions(scope)
  const [includeRegistry, setIncludeRegistry] = useState(false)
  const [selected, setSelected] = useState<string | null>(null)
  const graph = useMemo(() => (defs.data ? buildTypeGraph(defs.data, { includeRegistry }) : null), [defs.data, includeRegistry])
  const layout = useQuery({
    queryKey: ["layout:types", defs.data?.receiptSha256, includeRegistry],
    enabled: graph !== null,
    queryFn: () => (graph ? layeredLayout(graph.nodes.map((n) => ({ id: n.id, width: NODE_WIDTH, height: typeNodeHeight(n) })), graph.edges) : new Map()),
    staleTime: Infinity,
  })
  const flow = useMemo(() => (graph && layout.data ? typeFlow(graph, layout.data) : null), [graph, layout.data])
  const selectedNode = graph?.nodes.find((n) => n.id === selected)

  if (defs.isError) return <ErrorState error={defs.error} what={t("definitions.what")} onRetry={() => void defs.refetch()} />
  if (defs.isPending || !graph || !flow) return <GraphSkeleton />
  if (graph.nodes.length === 0) {
    return (
      <EmptyState icon={<Network />} title={t("explorer.noObjectTypes")}>
        <p>{t("explorer.typeGraphDescription")}</p>
      </EmptyState>
    )
  }
  return (
    <div className="flex h-full">
      <div className="relative min-w-0 flex-1">
        <GraphCanvas nodes={flow.nodes} edges={flow.edges} nodeTypes={typeNodeTypes} onNodeClick={(n) => setSelected(n.id)}>
          <div className="absolute top-3 start-3 z-10 flex items-center gap-2">
            <Button size="sm" variant={includeRegistry ? "primary" : "secondary"} onClick={() => setIncludeRegistry((v) => !v)} aria-pressed={includeRegistry}>
              {includeRegistry ? t("explorer.showingRegistryBase") : graph.hidden ? t("explorer.includeRegistryBaseMore", { count: graph.hidden }) : t("explorer.includeRegistryBase")}
            </Button>
            <span className="text-xs text-subtle-foreground tabular">
              {t("explorer.typeGraphCount", { count: graph.nodes.length, edgeCount: graph.edges.length })}
            </span>
          </div>
        </GraphCanvas>
      </div>
      {selectedNode ? (
        <aside className="w-80 shrink-0 overflow-y-auto border-s border-border-subtle bg-sidebar p-4" aria-label={t("explorer.details", { name: selectedNode.id })}>
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h2 className="truncate text-sm font-medium">{selectedNode.id}</h2>
              <p className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
                <Badge tone="outline">{selectedNode.form === "interface" ? t("graph.interface") : t("graph.objectType")}</Badge>
                {selectedNode.layer === "registry" ? t("explorer.registryBase") : t("explorer.tenant")}
              </p>
            </div>
            <Button size="icon" variant="ghost" aria-label={t("explorer.closeDetails")} onClick={() => setSelected(null)}>
              <X />
            </Button>
          </div>
          {selectedNode.description ? <p className="mt-3 text-xs text-muted-foreground">{selectedNode.description}</p> : null}
          <ul className="mt-4 divide-y divide-border-subtle rounded-md border border-border-subtle">
            {selectedNode.properties.map((p) => (
              <li key={p.id} className="flex items-center justify-between gap-2 px-2.5 py-1.5" title={p.extension ? t("graph.addedByExtension", { name: p.extension }) : undefined}>
                <code className={p.extension ? "truncate font-mono text-2xs text-primary/90" : "truncate font-mono text-2xs"}>
                  {p.extension ? "+ " : ""}
                  {p.id}
                </code>
                <span className="truncate text-2xs text-subtle-foreground">{p.type}</span>
              </li>
            ))}
          </ul>
          <div className="mt-4 flex flex-col gap-2">
            <Button size="sm" asChild>
              <Link to="/$orgId/$environmentId/definitions/$kind/$artifactId" params={{ ...scope, kind: "entity", artifactId: selectedNode.artifactId }}>
                {t("explorer.openDefinition")} <ArrowUpRight className="rtl-mirror" />
              </Link>
            </Button>
            {selectedNode.form === "objectType" ? (
              <Button size="sm" asChild>
                <Link to="/$orgId/$environmentId/objects/$objectType" params={{ ...scope, objectType: selectedNode.id }}>
                  {t("explorer.browseObjects")} <ArrowUpRight className="rtl-mirror" />
                </Link>
              </Button>
            ) : null}
          </div>
        </aside>
      ) : null}
    </div>
  )
}

const COLUMN_GAP = 140
const STACK_GAP = 32

function nodeHeight(rows: number) {
  return HEADER_HEIGHT + Math.max(rows, 1) * ROW_HEIGHT + 8
}

function stack<T>(items: readonly T[], x: number, rows: (item: T) => number): { item: T; x: number; y: number }[] {
  let y = 0
  return items.map((item) => {
    const at = { item, x, y }
    y += nodeHeight(rows(item)) + STACK_GAP
    return at
  })
}

function lineageFlow(lineage: DeclaredLineage): { nodes: (DatasetFlowNode | MapFlowNode | TargetFlowNode)[]; edges: Edge[] } {
  const col = (i: number) => i * (NODE_WIDTH + COLUMN_GAP)
  const mappedProps = new Map<string, Set<string>>()
  for (const m of lineage.maps) {
    const set = mappedProps.get(m.targetType) ?? new Set<string>()
    for (const r of m.rows) set.add(r.property)
    mappedProps.set(m.targetType, set)
  }
  const nodes: (DatasetFlowNode | MapFlowNode | TargetFlowNode)[] = [
    ...stack(lineage.sources, col(0), (s) => s.fields.length).map(({ item, x, y }): DatasetFlowNode => ({ id: `src:${item.id}`, type: "dataset", position: { x, y }, data: { dataset: item, name: item.id.split("#")[1] ?? item.id } })),
    ...stack(lineage.maps, col(1), (m) => m.rows.length).map(({ item, x, y }): MapFlowNode => ({ id: `map:${item.id}`, type: "map", position: { x, y }, data: { map: item } })),
    ...stack(lineage.targets, col(2), (t) => t.properties.length).map(({ item, x, y }): TargetFlowNode => ({ id: `tgt:${item.type}`, type: "target", position: { x, y }, data: { type: item.type, properties: item.properties, mapped: mappedProps.get(item.type) ?? new Set() } })),
  ]
  const edges: Edge[] = []
  for (const m of lineage.maps) {
    const source = lineage.sources.find((s) => s.id === m.sourceId)
    for (const r of m.rows) {
      if (source) {
        for (const field of r.inputs) {
          edges.push({ id: `e:${m.id}:${field}->${r.property}`, source: `src:${source.id}`, sourceHandle: `f:${field}`, target: `map:${m.id}`, targetHandle: `in:${r.property}`, type: "default", style: declaredEdge })
        }
      }
      edges.push({ id: `e:${m.id}:${r.property}->tgt`, source: `map:${m.id}`, sourceHandle: `out:${r.property}`, target: `tgt:${m.targetType}`, targetHandle: `p:${r.property}`, type: "default", style: declaredEdge })
    }
  }
  return { nodes, edges }
}

const lineageNodeTypes = { dataset: DatasetNodeView, map: MapNodeView, target: TargetNodeView }

export function LineagePage() {
  const t = useT()
  const scope = useScope()
  const defs = useDefinitions(scope)
  const lineage = useMemo(() => (defs.data ? buildDeclaredLineage(defs.data) : null), [defs.data])
  const flow = useMemo(() => (lineage ? lineageFlow(lineage) : null), [lineage])
  const [selected, setSelected] = useState<string | null>(null)
  const selectedMap = lineage?.maps.find((m) => `map:${m.id}` === selected)

  if (defs.isError) return <ErrorState error={defs.error} what={t("definitions.what")} onRetry={() => void defs.refetch()} />
  if (defs.isPending || !lineage || !flow) return <GraphSkeleton />
  if (lineage.maps.length === 0) {
    return (
      <EmptyState icon={<Network />} title={t("explorer.noMappings")}>
        <p>{t("explorer.lineageDescription")}</p>
      </EmptyState>
    )
  }
  return (
    <div className="flex h-full">
      <div className="relative min-w-0 flex-1">
        <GraphCanvas nodes={flow.nodes} edges={flow.edges} nodeTypes={lineageNodeTypes} onNodeClick={(n) => setSelected(n.id)}>
          <div className="absolute top-3 start-3 z-10 flex max-w-md items-center gap-3 text-xs text-subtle-foreground">
            <span className="flex gap-6">
              <span>{t("explorer.source")}</span>
              <span aria-hidden>→</span>
              <span>MappingIR</span>
              <span aria-hidden>→</span>
              <span>{t("findings.objectType")}</span>
            </span>
          </div>
        </GraphCanvas>
      </div>
      {selectedMap ? (
        <aside className="w-96 shrink-0 overflow-y-auto border-s border-border-subtle bg-sidebar p-4" aria-label={t("explorer.mappingDetails")}>
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h2 className="truncate text-sm font-medium">
                {selectedMap.dataset} → {selectedMap.targetType}
              </h2>
              <code className="font-mono text-2xs text-subtle-foreground">{selectedMap.mappingArtifactId}</code>
            </div>
            <Button size="icon" variant="ghost" aria-label={t("explorer.closeDetails")} onClick={() => setSelected(null)}>
              <X />
            </Button>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">{t("explorer.declaredExpressions")}</p>
          <dl className="mt-4 space-y-3">
            {selectedMap.rows.map((r) => (
              <div key={`${r.property}-${r.lifecycle}`}>
                <dt className="flex items-center gap-1.5 text-xs">
                  <code className="font-mono">{r.property}</code>
                  {r.lifecycle ? <Badge>{t("explorer.lifecycle")}</Badge> : null}
                  {r.inputs.length > 0 ? <span className="text-subtle-foreground">← {r.inputs.join(", ")}</span> : null}
                </dt>
                {/* Expressions are code: left to right and isolated, so `-12` cannot read as `12-` in Arabic. */}
                <dd dir="ltr" className={cn("mt-1 rounded-md border border-border-subtle bg-card px-2.5 py-1.5 font-mono text-2xs break-words whitespace-pre-wrap text-foreground/85")}>{r.expression}</dd>
              </div>
            ))}
          </dl>
          <div className="mt-4 flex flex-col gap-2">
            <Button size="sm" asChild>
              <Link to="/$orgId/$environmentId/definitions/$kind/$artifactId" params={{ ...scope, kind: "mapping", artifactId: selectedMap.mappingArtifactId }}>
                {t("explorer.openMappingDefinition")} <ArrowUpRight className="rtl-mirror" />
              </Link>
            </Button>
            <Button size="sm" asChild>
              <Link to="/$orgId/$environmentId/objects/$objectType" params={{ ...scope, objectType: selectedMap.targetType }}>
                {t("explorer.browseType", { name: selectedMap.targetType })} <ArrowUpRight className="rtl-mirror" />
              </Link>
            </Button>
          </div>
        </aside>
      ) : null}
    </div>
  )
}
